import celfinetLogo  from '../../../../assets/Cyient_Slate_Std_RGB.png'
import { useRef, useState, useEffect } from 'react'
import {
  Box, Button, CircularProgress, Dialog, DialogContent, IconButton,
  Menu, MenuItem, Slider, Tooltip, Typography,
} from '@mui/material'
import FullscreenIcon from '@mui/icons-material/Fullscreen'
import CloseIcon      from '@mui/icons-material/Close'
import DownloadIcon   from '@mui/icons-material/Download'
import TuneIcon       from '@mui/icons-material/Tune'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import { Line } from 'react-chartjs-2'
import { useDispatch, useSelector } from 'react-redux'
import AxiosRequest from '../../../../utils/AxiosRequest'
import config       from '../../../../configuration/configuration.js'
import {
  selectBeacon4VisibleSeries,
  toggleBeacon4Series,
  resetBeacon4HiddenSeries,
} from '../../reducers/Beacon4FiltersReducer'
import { selectBeacon4RagMap } from '../../reducers/Beacon4RagReducer'

/**
 * Small "↻" icon button that resets a single slider back to [0, 100] (the full
 * data range). Used inline next to each slider label so the user can clear that
 * one axis without touching the others.
 */
function ResetButton({ onClick, disabled, title = 'Reset to default range' }) {
  return (
    <Tooltip title={title}>
      <span>
        <IconButton
          size="small"
          onClick={onClick}
          disabled={disabled}
          sx={{ p: 0.25, ml: 0.25, color: disabled ? 'text.disabled' : 'text.secondary',
                '&:hover': { color: 'primary.main' } }}
        >
          <RestartAltIcon sx={{ fontSize: 13 }} />
        </IconButton>
      </span>
    </Tooltip>
  )
}

const COLOR_MAP = {
  '2G':     '#1a3a6e',
  '3G':     '#4fc3f7',
  '4G':     '#f57c00',
  '5G':     '#ffb74d',
  'Blended':'#212121',
  'value':  '#1565c0',
}

const SERIES_DEFS = [
  { key: '2G',       color: '#1a3a6e', dash: false },
  { key: '3G',       color: '#4fc3f7', dash: false },
  { key: '4G',       color: '#f57c00', dash: false },
  { key: '5G',       color: '#ffb74d', dash: false },
  { key: 'Blended',  color: '#212121', dash: true  },
  { key: 'RAG Green',color: '#2e7d32', dash: true  },
  { key: 'RAG Red',  color: '#c62828', dash: true  },
]


// Map chart title prefix to RAG field names
const RAG_KEY_MAP = {
  avail: { green: 'availGreen', red: 'availRed' },
  vssr:  { green: 'vssrGreen',  red: 'vssrRed'  },
  dssr:  { green: 'dssrGreen',  red: 'dssrRed'  },
  vdcr:  { green: 'vdcrGreen',  red: 'vdcrRed'  },
  ddcr:  { green: 'ddcrGreen',  red: 'ddcrRed'  },
}

function buildRagDatasets(ragMap, labels, kpiKey, visibleSeries = null) {
  const fields = RAG_KEY_MAP[kpiKey]
  if (!fields || !Object.keys(ragMap).length) return []

  // For each label (date), find the corresponding month in ragMap
  const greenData = labels.map(label => {
    const monthKey = label.slice(0, 7)  // 'YYYY-MM'
    return ragMap[monthKey]?.[fields.green] ?? null
  })
  const redData = labels.map(label => {
    const monthKey = label.slice(0, 7)
    return ragMap[monthKey]?.[fields.red] ?? null
  })

  // Only add if we have at least some values
  const hasGreen = greenData.some(v => v != null)
  const hasRed   = redData.some(v => v != null)
  const datasets = []

  if (hasGreen) datasets.push({
    label: 'RAG Green', data: greenData,
    borderColor: '#2e7d32', borderWidth: 1.5, borderDash: [6, 3],
    pointRadius: 0, lineTension: 0, fill: false,
    hidden: visibleSeries !== null && !visibleSeries.includes('RAG Green'),
  })
  if (hasRed) datasets.push({
    label: 'RAG Red', data: redData,
    borderColor: '#c62828', borderWidth: 1.5, borderDash: [6, 3],
    pointRadius: 0, lineTension: 0, fill: false,
    hidden: visibleSeries !== null && !visibleSeries.includes('RAG Red'),
  })
  return datasets
}

// Group series by unit so we can decide if a second axis is needed.
// % goes to the left axis, anything else (#, erl, MB, ...) goes to the right.
function isPctUnit(u) { return (u ?? '').trim() === '%' }

// A series is drawn as a filled context/background area (Traffic in the
// Performance left panel, Availability-as-breakdown in the right panel) —
// everything else, including a panel's own Main KPI, is a plain line.
//
// Source of truth, in order:
//   1. s.chartStyle === 'area' — explicit, admin-controlled choice (KPI
//      Manager > Presentation). This is what the user actually set and
//      always wins when present.
//   2. sourceKpiKey + isPrimary — fallback for any KPI saved before
//      chart_style existed and not yet re-saved through the admin form.
//      NOT by display name: a Main KPI can legitimately be called
//      "2G Availability Ericsson", and matching on the word "availability"
//      in that name would wrongly fill it in as if it were a support series.
//   3. label text — last-resort fallback for callers that pass neither of
//      the above (Blended's series are just '2G'/'3G'/'4G'/'5G'/'Blended',
//      so this never actually fires there — it exists purely so the
//      function degrades sanely rather than throwing for an unexpected shape).
const CONTEXT_KPI_KEYS = new Set(['vtraf', 'dtraf', 'avail'])

function isContextSeries(s) {
  if (s && typeof s === 'object' && 'chartStyle' in s && s.chartStyle) {
    return s.chartStyle === 'area'
  }
  if (s && 'sourceKpiKey' in s) {
    return !s.isPrimary && CONTEXT_KPI_KEYS.has(s.sourceKpiKey)
  }
  const l = (typeof s === 'string' ? s : s?.key ?? '').toLowerCase()
  return l.includes('traffic') || l.includes('availability')
}

// Convert hex colour to rgba with given alpha (for area fill)
function hexToRgba(hex, alpha) {
  if (!hex || !hex.startsWith('#') || hex.length !== 7) return `rgba(21,101,192,${alpha})`
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return `rgba(${r},${g},${b},${alpha})`
}

function buildDatasets(series, visibleSeries) {
  return series.map(s => {
    const color  = s.color ?? COLOR_MAP[s.key] ?? '#1565c0'
    const isCtx  = isContextSeries(s)
    return {
      label:           s.key,
      data:            [...(s.data ?? [])],
      unit:            s.unit ?? '',
      // Prefer the colour provided on the series object (Performance), fall back to the global COLOR_MAP (Blended)
      borderColor:     color,
      backgroundColor: isCtx ? hexToRgba(color, 0.18) : 'transparent',
      borderWidth:     s.key === 'Blended' ? 2.5 : (isCtx ? 1 : 1.5),
      borderDash:      s.key === 'Blended' ? [6, 3] : [],
      pointRadius:     0,
      lineTension:     0,
      fill:            isCtx ? 'origin' : false,
      hidden:          visibleSeries !== null && !visibleSeries.includes(s.key),
      yAxisID:         isPctUnit(s.unit) ? 'y-pct' : 'y-other',
      // Draw context areas below the line series so they don't cover them
      order:           isCtx ? 2 : 1,
    }
  })
}

// Determine which Y axes are present given the currently VISIBLE datasets.
// This is the single source of truth shared by buildOptions() (which builds
// the Chart.js scale config) and sanitizeDatasetAxes() (which makes sure
// every dataset — including hidden ones — points at an axis that actually
// exists). Without this, hiding the last series on one axis drops that
// scale from `options.scales.yAxes`, but the now-hidden dataset object is
// NOT removed from chart.data (it stays so the legend can toggle it back
// on) and keeps referencing the now-missing scale id. Chart.js v2 still
// walks every dataset during layout/update regardless of its hidden flag,
// so it crashes with "Cannot read properties of undefined (reading
// 'options')" the moment it can't resolve that scale.
function resolveAxisState(datasets) {
  const visible = datasets.filter(d => !d.hidden)
  const usedAxes = new Set(visible.map(d => d.yAxisID).filter(Boolean))
  const hasPctAxis   = usedAxes.has('y-pct')
  const hasOtherAxis = usedAxes.has('y-other')
  const dualAxis     = hasPctAxis && hasOtherAxis

  // Build axis labels dynamically from the actual series units. If a single
  // axis is sharing multiple units (e.g. erl + MB), join them with " · ".
  const uniqueUnits = (axisId) => [...new Set(
    visible.filter(d => d.yAxisID === axisId).map(d => d.unit ?? '').filter(Boolean)
  )]
  const pctUnitsLabel   = uniqueUnits('y-pct').join(' · ')   || '%'
  const otherUnitsLabel = uniqueUnits('y-other').join(' · ') || '#'

  // The id every dataset must fall back to when its own axis no longer
  // exists (e.g. all its series got hidden, or — for RAG lines, which never
  // set yAxisID at all — there's no percentage axis left to default onto).
  const fallbackId = hasPctAxis ? 'y-pct' : hasOtherAxis ? 'y-other' : 'y-fallback'

  return { hasPctAxis, hasOtherAxis, dualAxis, pctUnitsLabel, otherUnitsLabel, fallbackId }
}

// Re-point every dataset's yAxisID at an axis that will actually exist in
// the chart's scales (see resolveAxisState above for why this is needed).
// Hidden datasets keep their data — only the axis id changes — so toggling
// the legend back on still works exactly as before.
function sanitizeDatasetAxes(datasets, axisState) {
  const { hasPctAxis, hasOtherAxis, fallbackId } = axisState
  return datasets.map(d => {
    const stillExists = (d.yAxisID === 'y-pct' && hasPctAxis) || (d.yAxisID === 'y-other' && hasOtherAxis)
    return stillExists ? d : { ...d, yAxisID: fallbackId }
  })
}

function buildOptions(unit, yBounds = {}, pinnedActive = false, datasets = []) {
  const { pctMin, pctMax, otherMin, otherMax } = yBounds
  const { hasPctAxis, hasOtherAxis, dualAxis, pctUnitsLabel, otherUnitsLabel } = resolveAxisState(datasets)

  const yAxes = []
  if (hasPctAxis) {
    yAxes.push({
      id:        'y-pct',
      position:  'left',
      ticks: {
        fontSize:      9,
        maxTicksLimit: 6,
        ...(pctMin != null ? { min: pctMin } : {}),
        ...(pctMax != null ? { max: pctMax } : {}),
        callback: v => Number(v).toFixed(2),
      },
      gridLines: { color: 'rgba(0,0,0,.05)' },
      scaleLabel: dualAxis ? { display: true, labelString: pctUnitsLabel, fontSize: 9, fontColor: '#8fa3bc' } : { display: false },
    })
  }
  if (hasOtherAxis) {
    yAxes.push({
      id:        'y-other',
      position:  dualAxis ? 'right' : 'left',
      ticks: {
        fontSize:      9,
        maxTicksLimit: 6,
        ...(otherMin != null ? { min: otherMin } : {}),
        ...(otherMax != null ? { max: otherMax } : {}),
        callback: v => Number(v).toLocaleString(),
      },
      gridLines: { display: false },
      scaleLabel: dualAxis ? { display: true, labelString: otherUnitsLabel, fontSize: 9, fontColor: '#8fa3bc' } : { display: false },
    })
  }
  // Default fallback (no visible datasets at all) — single axis, format
  // with unit suffix. Needs an explicit id so sanitizeDatasetAxes() can
  // point every (hidden) dataset at it — otherwise Chart.js falls back to
  // its own default scale id, which dataset.yAxisID === 'y-pct'/'y-other'
  // would never match, reproducing the same crash this fixes.
  if (yAxes.length === 0) {
    // Use the percentage bounds as a generic default since most KPIs are %
    yAxes.push({
      id: 'y-fallback',
      ticks: {
        fontSize:      9,
        maxTicksLimit: 6,
        ...(pctMin != null ? { min: pctMin } : {}),
        ...(pctMax != null ? { max: pctMax } : {}),
        callback: v => Number(v).toFixed(2),
      },
      gridLines: { color: 'rgba(0,0,0,.05)' },
    })
  }

  return {
    responsive:          true,
    maintainAspectRatio: false,
    animation:           { duration: 300 },
    legend:              { display: false },
    tooltips: {
      mode:            'index',
      intersect:       false,
      backgroundColor: 'rgba(255,255,255,0.97)',
      borderColor:     'rgba(0,0,0,0.1)',
      borderWidth:     1,
      titleFontColor:  '#8fa3bc',
      titleFontSize:   10,
      bodyFontColor:   '#1a2233',
      bodyFontSize:    11,
      xPadding:        10,
      yPadding:        10,
      caretSize:       5,
      cornerRadius:    6,
      callbacks: {
        label: (item, data) => {
          const ds       = data.datasets[item.datasetIndex]
          const dsUnit   = ds.unit ?? unit ?? ''
          const value    = Number(item.yLabel)
          const formatted = isPctUnit(dsUnit) ? value.toFixed(2) : value.toLocaleString(undefined, { maximumFractionDigits: 2 })
          return ` ${ds.label}: ${formatted}${dsUnit}`
        },
        labelColor: (item, chart) => {
          const ds = chart.data.datasets[item.datasetIndex]
          return { borderColor: ds.borderColor, backgroundColor: ds.borderColor }
        },
      },
    },
    scales: {
      xAxes: [{ ticks: { fontSize: 9, maxRotation: 45, autoSkip: true, maxTicksLimit: 12 }, gridLines: { display: false } }],
      yAxes,
    },
  }
}

function LegendRow({ d, visibleSeries, onToggle }) {
  const isHidden = visibleSeries !== null && !visibleSeries.includes(d.key)
  return (
    <Box onClick={() => onToggle(d.key)} sx={{
      display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.5,
      borderRadius: 1, cursor: 'pointer',
      opacity: isHidden ? 0.35 : 1, transition: 'all 0.15s',
      '&:hover': { bgcolor: 'action.hover' },
    }}>
      <Box sx={{
        width: 22, flexShrink: 0,
        height: d.dash ? 0 : 2.5, borderRadius: 1,
        bgcolor: d.dash ? 'transparent' : d.color,
        borderTop: d.dash ? `2px dashed ${d.color}` : 'none',
      }} />
      <Typography sx={{ fontSize: 11, fontWeight: isHidden ? 400 : 600, color: isHidden ? 'text.disabled' : d.color }}>
        {d.key}
      </Typography>
    </Box>
  )
}

const RAG_DEFS  = SERIES_DEFS.filter(d =>  d.key.startsWith('RAG'))

function ModalLegend({ series, visibleSeries, onToggle, onClear, hasRag }) {
  // Build the legend rows straight from the series actually rendered on this
  // chart, instead of cross-referencing the fixed Blended tech list. That
  // fixed list (2G/3G/4G/5G/Blended) only matches Blended series keys — for
  // Performance, series keys are KPI names (e.g. "2G Availability"), so the
  // old lookup always came up empty and the modal legend showed no series at
  // all. Each series already carries its own colour (Performance) or falls
  // back to the shared COLOR_MAP (Blended), matching buildDatasets().
  const seriesDefs = series.map(s => ({
    key:   s.key,
    color: s.color ?? COLOR_MAP[s.key] ?? '#1565c0',
    dash:  s.key === 'Blended',
  }))

  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column', gap: 0.25,
      // Explicit fixed width (not just minWidth) + flexShrink: 0 — this
      // column sits next to the chart inside a `display:flex` row that gets
      // captured by html2canvas for chart export. html2canvas re-implements
      // its own layout engine rather than using the real browser layout, and
      // it can under-measure flex children whose width depends on
      // flex-grow/auto-basis, sometimes rendering them at ~0 width and
      // effectively dropping them from the exported image. A concrete pixel
      // width removes that ambiguity so the legend reliably shows up in
      // PDF/PNG/PPT exports, not just on screen.
      width: 160, flexShrink: 0,
      pl: 2, borderLeft: '1px solid', borderColor: 'divider',
    }}>

      <Typography sx={{ fontSize: 9, fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.5 }}>
        Series
      </Typography>
      {seriesDefs.map(d => <LegendRow key={d.key} d={d} visibleSeries={visibleSeries} onToggle={onToggle} />)}

      {hasRag && (
        <>
          <Typography sx={{ fontSize: 9, fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.08em', mt: 1.5, mb: 0.5 }}>
            RAG Thresholds
          </Typography>
          {RAG_DEFS.map(d => <LegendRow key={d.key} d={d} visibleSeries={visibleSeries} onToggle={onToggle} />)}
        </>
      )}

      <Box sx={{ mt: 1 }}>
        {visibleSeries !== null ? (
          <Box onClick={onClear} sx={{
            fontSize: 10, fontWeight: 600, color: 'primary.main', cursor: 'pointer',
            textAlign: 'center', py: 0.5, borderRadius: 1,
            border: '1px solid', borderColor: 'primary.light',
            '&:hover': { bgcolor: 'primary.light' }, transition: 'all .15s',
          }}>
            ✕ Show all
          </Box>
        ) : (
          <Typography sx={{ fontSize: 9, color: 'text.disabled', textAlign: 'center', fontStyle: 'italic' }}>
            Click to isolate
          </Typography>
        )}
      </Box>
    </Box>
  )
}

export default function Beacon4KpiLineChart({ title, seriesDto, height = 220, kpiKey }) {
  const fillHeight = height === '100%'
  const dispatch            = useDispatch()
  const globalVisibleSeries = useSelector(selectBeacon4VisibleSeries)
  const ragMap              = useSelector(selectBeacon4RagMap)
  const [localVisibleSeries, setLocalVisibleSeries] = useState(null)

  // Performance's two panels ("KPI Performance" / "KPI Failures") each get a
  // composite kpiKey like `perf_left_2G_vssr` / `perf_right_2G_vssr` and have
  // entirely disjoint series names. The GLOBAL visibleSeries/toggleBeacon4Series
  // slice in Beacon4FiltersReducer is designed for Blended, where several
  // chart cards intentionally mirror the same 2G/3G/4G/5G isolate state.
  // Reusing that same global slice for Performance meant isolating a series
  // in one panel's legend also hid every series in the OTHER panel (since
  // none of its names were ever in the isolated array) — breaking that other
  // panel's legend until "Show all" was clicked. Performance charts use fully
  // independent LOCAL state instead; Blended's existing behaviour (global
  // slice) is untouched.
  const isPerformanceChart = typeof kpiKey === 'string' && kpiKey.startsWith('perf_')
  const visibleSeries = isPerformanceChart ? localVisibleSeries : globalVisibleSeries
  const [open,          setOpen]          = useState(false)
  const [range,         setRange]         = useState([0, 100])
  const [yRange,        setYRange]        = useState([0, 100])
  const [y2Range,       setY2Range]       = useState([0, 100])
  const [sliders,       setSliders]       = useState(false)
  const [pinnedTooltip,      setPinnedTooltip]      = useState(null)
  const [modalPinnedTooltip, setModalPinnedTooltip] = useState(null)
  const legendRef         = useRef(null)
  const modalChartBoxRef = useRef(null)
  const modalLineRef     = useRef(null)
  const chartRef         = useRef(null)
  const chartBoxRef      = useRef(null)

  // Force Chart.js to resize when the container resizes (grid layouts often
  // change width without firing window.resize, leaving Chart.js stale).
  // We wrap the resize in requestAnimationFrame and compare last-seen
  // dimensions to break the classic "ResizeObserver loop completed with
  // undelivered notifications" feedback — Chart.js's resize() can itself
  // mutate the box, which would otherwise fire the observer again.
  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') return
    const el = chartBoxRef.current
    if (!el) return
    let raf = 0
    let lastW = 0, lastH = 0
    const ro = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect
      if (!rect) return
      // Only react when the size actually changed (>0.5px to absorb sub-pixel
      // rounding from flex/clamp containers).
      if (Math.abs(rect.width - lastW) < 0.5 && Math.abs(rect.height - lastH) < 0.5) return
      lastW = rect.width
      lastH = rect.height
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(() => {
        const ci = chartRef.current?.chartInstance
        if (ci) try { ci.resize() } catch (_) {}
      })
    })
    ro.observe(el)
    return () => { cancelAnimationFrame(raf); ro.disconnect() }
  }, [])

  // Reset Y sliders whenever the set of series changes (e.g. user toggled the
  // legend). Without this, a zoomed-in slider keeps clipping the axis even
  // after the underlying data bounds have shifted.
  const seriesSignature = (seriesDto?.series ?? []).map(s => s.key).join('|')
  useEffect(() => {
    setYRange([0, 100])
    setY2Range([0, 100])
    setLocalVisibleSeries(null)
  }, [seriesSignature])


  const [exportAnchor, setExportAnchor] = useState(null)
  const [exporting,    setExporting]    = useState(false)

  const safeTitle = title.replace(/[^a-z0-9]/gi, '_')

  const captureChart = async () => {
    const chartEl = modalChartBoxRef.current
    if (!chartEl) return null
    const html2canvas = (await import('html2canvas')).default

    const chartCanvas = await html2canvas(chartEl, { scale: 3, backgroundColor: '#ffffff', logging: false })

    // Capture the legend SEPARATELY and composite it alongside the chart
    // ourselves, rather than capturing both in one pass. html2canvas
    // re-implements CSS layout instead of using the real browser layout, and
    // is known to misjudge a flex sibling whose height depends on stretching
    // to match another flex item (exactly the chart/legend row here) —
    // sometimes rendering it at ~0 size and dropping it from the image
    // entirely. Capturing each region independently and drawing them onto
    // one canvas ourselves removes that ambiguity completely.
    let legendCanvas = null
    if (legendRef.current) {
      try {
        legendCanvas = await html2canvas(legendRef.current, { scale: 3, backgroundColor: '#ffffff', logging: false })
      } catch (_) { /* fall back to chart-only below if legend capture fails */ }
    }

    if (!legendCanvas) return chartCanvas

    const combined = document.createElement('canvas')
    combined.width  = chartCanvas.width + legendCanvas.width
    combined.height = Math.max(chartCanvas.height, legendCanvas.height)
    const ctx = combined.getContext('2d')
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, combined.width, combined.height)
    ctx.drawImage(chartCanvas, 0, 0)
    ctx.drawImage(legendCanvas, chartCanvas.width, 0)
    return combined
  }

  const exportChartPDF = async () => {
    setExportAnchor(null)
    setExporting(true)
    try {
      const canvas = await captureChart()
      if (!canvas) return
      const { jsPDF } = await import('jspdf')
      // Anchor the PDF page to the canvas's EXACT pixel dimensions (no
      // arbitrary scaling) and pick orientation from the real aspect ratio,
      // instead of hardcoding 'landscape'. addImage then draws at those same
      // dimensions, so there is no separate scale factor anywhere that could
      // cause the image to be rendered larger than the declared page and
      // have its right-hand edge (where the legend sits) pushed off it.
      const w = canvas.width, h = canvas.height
      const pdf = new jsPDF({ orientation: w >= h ? 'landscape' : 'portrait', unit: 'px', format: [w, h] })
      pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, w, h)
      pdf.save(`${safeTitle}.pdf`)
    } finally { setExporting(false) }
  }

  const exportChartPNG = async () => {
    setExportAnchor(null)
    setExporting(true)
    try {
      const canvas = await captureChart()
      if (!canvas) return
      const link    = document.createElement('a')
      link.download = `${safeTitle}.png`
      link.href     = canvas.toDataURL('image/png')
      link.click()
    } finally { setExporting(false) }
  }

  const exportChartPPT = async () => {
    setExportAnchor(null)
    setExporting(true)
    try {
      const canvas = await captureChart()
      if (!canvas) return
      const imageBase64 = canvas.toDataURL('image/jpeg', 0.95)

      const res = await AxiosRequest({
        method:       'POST',
        url:          `${config.API_URL}Beacon4Export/Pptx`,
        data:         { title, charts: [{ name: title, imageBase64 }] },
        responseType: 'blob',
      })

      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      })
      const url  = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href     = url
      link.download = `${safeTitle}.pptx`
      link.click()
      URL.revokeObjectURL(url)
    } finally { setExporting(false) }
  }

  const handleToggle = (key) => {
    if (isPerformanceChart) {
      // Local isolate: clicking a series shows only that one; clicking the
      // same one again (or the same isolated series) restores "show all".
      setLocalVisibleSeries(prev => (prev && prev.length === 1 && prev[0] === key) ? null : [key])
    } else {
      dispatch(toggleBeacon4Series(key))
    }
  }

  // Same "show all" logic used by the legend's own "✕ Show all" button —
  // extracted so the modal's close handlers can call it too (see closeModal
  // below). Isolating a series is meant to be a temporary, modal-scoped
  // action: closing the maximized view clears it instead of leaving the
  // main/inline chart filtered by whatever was isolated inside the modal.
  const resetSeriesFilter = () => {
    if (isPerformanceChart) setLocalVisibleSeries(null)
    else dispatch(resetBeacon4HiddenSeries())
  }

  const closeModal = () => {
    setOpen(false)
    setModalPinnedTooltip(null)
    const ci = modalLineRef.current?.chartInstance
    if (ci) { ci.options.tooltips.enabled = true; ci.update(0) }
    resetSeriesFilter()
  }

  const handleRangeChange = (v) => { setRange(v); setPinnedTooltip(null) }

  // Reset helpers: each slider has its own back-to-default action, plus a
  // "Reset all" that brings every axis back to the full data range in one shot.
  // The default for every slider is [0, 100] (i.e. 0%-100% of the observed
  // data range), so reset means simply restoring that pair.
  const isXReset  = range[0]   === 0 && range[1]   === 100
  const isY1Reset = yRange[0]  === 0 && yRange[1]  === 100
  const isY2Reset = y2Range[0] === 0 && y2Range[1] === 100
  const allReset  = isXReset && isY1Reset && isY2Reset
  const resetX    = () => { setRange([0, 100]); setPinnedTooltip(null); setModalPinnedTooltip(null) }
  const resetY1   = () => setYRange([0, 100])
  const resetY2   = () => setY2Range([0, 100])
  const resetAll  = () => { resetX(); resetY1(); resetY2() }

  if (!seriesDto) {
    return (
      <Box sx={{ bgcolor: '#fff', border: '1px solid #e3e8ef', borderRadius: '12px', p: '12px 14px', boxShadow: '0 1px 3px rgba(0,0,0,.04)' }}>
        <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#4a5568', textAlign: 'center', mb: 1 }}>{title}</Typography>
        <Box sx={{ height, bgcolor: '#f7f8fa', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography sx={{ fontSize: 11, color: '#8fa3bc' }}>Loading…</Typography>
        </Box>
      </Box>
    )
  }

  const { labels, series, unit } = seriesDto
  const ragDatasets = kpiKey ? buildRagDatasets(ragMap, labels ?? [], kpiKey, visibleSeries) : []
  const datasets    = [...buildDatasets(series, visibleSeries), ...ragDatasets]

  // Slice by X range
  const totalPts       = (labels ?? []).length
  const startIdx       = Math.floor(range[0] / 100 * totalPts)
  const endIdx         = Math.ceil(range[1]  / 100 * totalPts)
  const slicedLabels   = [...(labels ?? [])].slice(startIdx, endIdx)
  const slicedDatasets = datasets.map(d => ({ ...d, data: (d.data ?? []).slice(startIdx, endIdx) }))

  // Y bounds — computed PER AXIS so each slider controls its own data range
  function axisBounds(datasets, axisId) {
    const vals = datasets
      .filter(d => !d.hidden && d.yAxisID === axisId)
      .flatMap(d => (d.data ?? []).filter(v => v != null))
    if (vals.length === 0) return null
    const min = vals.reduce((a, b) => a < b ? a : b, vals[0])
    const max = vals.reduce((a, b) => a > b ? a : b, vals[0])
    return { min, max, rng: (max - min) || 1 }
  }
  const pctBounds   = axisBounds(slicedDatasets, 'y-pct')
  const otherBounds = axisBounds(slicedDatasets, 'y-other')
  const hasPctAxis   = pctBounds   != null
  const hasOtherAxis = otherBounds != null
  const dualAxis     = hasPctAxis && hasOtherAxis

  // Dynamic unit labels for the Y sliders — read from the actual series units
  const visibleDs = slicedDatasets.filter(d => !d.hidden)
  const pctSliderUnit   = [...new Set(visibleDs.filter(d => d.yAxisID === 'y-pct').map(d => d.unit ?? '').filter(Boolean))].join(' · ')   || '%'
  const otherSliderUnit = [...new Set(visibleDs.filter(d => d.yAxisID === 'y-other').map(d => d.unit ?? '').filter(Boolean))].join(' · ') || '#'

  // Apply slider percentages to each axis' real bounds
  const pctMin  = hasPctAxis   ? pctBounds.min   + (yRange[0]  / 100) * pctBounds.rng   : 0
  const pctMax  = hasPctAxis   ? pctBounds.min   + (yRange[1]  / 100) * pctBounds.rng   : 100
  const otherMin = hasOtherAxis ? otherBounds.min + (y2Range[0] / 100) * otherBounds.rng : 0
  const otherMax = hasOtherAxis ? otherBounds.min + (y2Range[1] / 100) * otherBounds.rng : 100

  // Bounds passed to Chart.js only when sliders are active
  const yBounds = sliders ? {
    pctMin:   hasPctAxis   ? pctMin   : undefined,
    pctMax:   hasPctAxis   ? pctMax   : undefined,
    otherMin: hasOtherAxis ? otherMin : undefined,
    otherMax: hasOtherAxis ? otherMax : undefined,
  } : {}

  const options = buildOptions(unit, yBounds, false, slicedDatasets)

  // The data actually handed to Chart.js: same datasets, but with any
  // orphaned yAxisID (e.g. 'y-other' after every non-% series got hidden,
  // or a RAG line that never set yAxisID) re-pointed at a scale that still
  // exists in `options`. See resolveAxisState()/sanitizeDatasetAxes() above.
  const chartDatasets = sanitizeDatasetAxes(slicedDatasets, resolveAxisState(slicedDatasets))

  // Legacy single-axis values still used by UI labels: prefer whichever axis is active
  const yMin = hasPctAxis ? pctMin : otherMin
  const yMax = hasPctAxis ? pctMax : otherMax

  // Forces react-chartjs-2 to fully unmount/remount the canvas (a fresh
  // Chart.js instance) whenever the SET of active axes changes — e.g. going
  // from dual-axis (%, #/erl/MB) down to single-axis after isolating one
  // series in the legend. Chart.js v2's own update() path is meant to add/
  // remove scales based on options.scales.yAxes, but in practice a scale
  // that disappears from that array can still leave a stale axis rendered
  // (exactly the "still shows 2 axes after filtering to 1 series" bug this
  // fixes) rather than being torn down. Changing `key` sidesteps trusting
  // that in-place update entirely: React discards the old canvas/instance
  // and Chart.js builds a brand new one from scratch with only the scales
  // that exist right now, so there's nothing stale left to linger.
  const axisKey = `${hasPctAxis ? 1 : 0}${hasOtherAxis ? 1 : 0}`

  return (
    <>
      <Box sx={{ bgcolor: '#fff', border: '1px solid #e3e8ef', borderRadius: '12px', p: '12px 14px 10px', boxShadow: '0 1px 3px rgba(0,0,0,.04)', ...(fillHeight && { height: '100%', display: 'flex', flexDirection: 'column' }) }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1, position: 'relative' }}>
          <Typography onClick={() => setOpen(true)} sx={{ fontSize: 11, fontWeight: 600, color: '#4a5568', textAlign: 'center', cursor: 'pointer', '&:hover': { color: 'primary.main' } }}>
            {title}
          </Typography>
          <Box sx={{ position: 'absolute', right: 0, display: 'flex', gap: 0.25 }}>
            <IconButton size="small" onClick={() => setSliders(v => !v)}
              sx={{ p: 0.25, color: sliders ? 'primary.main' : 'text.disabled', '&:hover': { color: 'primary.main' } }}>
              <TuneIcon sx={{ fontSize: 15 }} />
            </IconButton>
            <IconButton size="small" onClick={() => setOpen(true)}
              sx={{ p: 0.25, color: 'text.disabled', '&:hover': { color: 'primary.main' } }}>
              <FullscreenIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Box>
        </Box>
        <Box
          ref={chartBoxRef}
          onClick={e => {
            const chart = chartRef.current?.chartInstance
            if (!chart || !chartBoxRef.current) return
            const rect  = chartBoxRef.current.getBoundingClientRect()
            const clickX = e.clientX - rect.left
            const clickY = e.clientY - rect.top
            const pts   = chart.getElementsAtXAxis(e.nativeEvent)
            if (!pts?.length) {
              const ci = chartRef.current?.chartInstance
              if (ci) { ci.options.tooltips.enabled = true; ci.update(0) }
              setPinnedTooltip(null)
              return
            }
            const idx   = pts[0]._index
            const label = chart.data.labels[idx]
            const ptX   = pts[0]._view?.x ?? clickX
            const ptY   = pts[0]._view?.y ?? clickY
            const items = chart.data.datasets
              .filter(ds => !ds.hidden)
              .map(ds => ({ key: ds.label, value: ds.data[idx], color: ds.borderColor }))
              .filter(it => it.value != null)
            const ci = chartRef.current?.chartInstance
            if (ci) { ci.options.tooltips.enabled = false; ci.update(0) }
            setPinnedTooltip({ label, items, x: ptX, y: ptY })
          }}
          sx={{ position: 'relative', height }}>
          {/* Watermark */}
          <Box sx={{
            position: 'absolute', top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            pointerEvents: 'none', zIndex: 0,
            width: '40%', display: 'flex', justifyContent: 'center', opacity: 0.05,
          }}>
            <img src={celfinetLogo} alt="" style={{ width: '100%' }} />
          </Box>
          <Line key={axisKey} ref={chartRef} data={{ labels: slicedLabels, datasets: chartDatasets }} options={options} />
          {pinnedTooltip && (
            <Box sx={{
              position: 'absolute',
              left: Math.min(pinnedTooltip.x + 8, 999),
              top:  Math.max(pinnedTooltip.y - 20, 0),
              transform: pinnedTooltip.x > 150 ? 'translateX(-100%)' : 'none',
              zIndex: 10, pointerEvents: 'auto',
              bgcolor: 'rgba(255,255,255,0.97)', border: '1px solid', borderColor: 'divider',
              borderRadius: 1.5, p: '8px 10px', boxShadow: '0 4px 12px rgba(0,0,0,.15)',
              minWidth: 130,
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'text.secondary' }}>
                  {pinnedTooltip.label}
                </Typography>
                <Box onClick={e => {
                  e.stopPropagation()
                  const ci = chartRef.current?.chartInstance
                  if (ci) { ci.options.tooltips.enabled = true; ci.update(0) }
                  setPinnedTooltip(null)
                }}
                  sx={{ fontSize: 11, cursor: 'pointer', color: 'text.disabled', ml: 1, lineHeight: 1, '&:hover': { color: 'text.primary' } }}>
                  ✕
                </Box>
              </Box>
              {pinnedTooltip.items.map(it => (
                <Box key={it.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: '1px' }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: it.color, flexShrink: 0 }} />
                  <Typography sx={{ fontSize: 10, color: 'text.secondary', flex: 1 }}>{it.key}</Typography>
                  <Typography sx={{ fontSize: 10, fontWeight: 600, color: 'text.primary' }}>
                    {Number(it.value).toFixed(2)}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>

              {sliders && totalPts > 0 && (
          <Box sx={{ px: 1.5, pt: 0.5, pb: 0.5 }}>
            {/* Header: "Reset all" button — disabled when nothing has been moved */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mb: 0.25 }}>
              <Button
                size="small"
                onClick={resetAll}
                disabled={allReset}
                startIcon={<RestartAltIcon sx={{ fontSize: 13 }} />}
                sx={{
                  py: 0.25, px: 0.75, fontSize: 9, textTransform: 'none',
                  color: allReset ? 'text.disabled' : 'text.secondary',
                  '&:hover': { color: 'primary.main', bgcolor: 'transparent' },
                }}
              >
                Reset all
              </Button>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            {/* X slider */}
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.25 }}>
                <Typography sx={{ fontSize: 9, color: 'text.disabled', flex: 1 }}>X — Date range</Typography>
                <ResetButton onClick={resetX} disabled={isXReset} />
              </Box>
              <Slider value={range} onChange={(_, v) => handleRangeChange(v)} size="small" disableSwap
                sx={{ color: 'primary.main', height: 3, '& .MuiSlider-thumb': { width: 10, height: 10 }, '& .MuiSlider-rail': { opacity: 0.2 } }}
              />
              <Typography sx={{ fontSize: 9, color: 'text.disabled', textAlign: 'center' }}>
                {slicedLabels[0] ?? ''} → {slicedLabels[slicedLabels.length - 1] ?? ''}
              </Typography>
            </Box>
            {/* Y1 slider — % axis (always when % data exists) */}
            {hasPctAxis && (
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.25 }}>
                  <Typography sx={{ fontSize: 9, color: 'text.disabled', flex: 1 }}>
                    Y1 — Range {dualAxis ? `(${pctSliderUnit})` : ''}
                  </Typography>
                  <ResetButton onClick={resetY1} disabled={isY1Reset} />
                </Box>
                <Slider value={yRange} onChange={(_, v) => setYRange(v)} size="small" disableSwap
                  sx={{ color: 'secondary.main', height: 3, '& .MuiSlider-thumb': { width: 10, height: 10 }, '& .MuiSlider-rail': { opacity: 0.2 } }}
                />
                <Typography sx={{ fontSize: 9, color: 'text.disabled', textAlign: 'center' }}>
                  {pctMin.toFixed(2)} → {pctMax.toFixed(2)}
                </Typography>
              </Box>
            )}
            {/* Y2 slider — other axis (#/erl/MB), only when dual axis */}
            {dualAxis && (
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.25 }}>
                  <Typography sx={{ fontSize: 9, color: 'text.disabled', flex: 1 }}>Y2 — Range ({otherSliderUnit})</Typography>
                  <ResetButton onClick={resetY2} disabled={isY2Reset} />
                </Box>
                <Slider value={y2Range} onChange={(_, v) => setY2Range(v)} size="small" disableSwap
                  sx={{ color: 'warning.main', height: 3, '& .MuiSlider-thumb': { width: 10, height: 10 }, '& .MuiSlider-rail': { opacity: 0.2 } }}
                />
                <Typography sx={{ fontSize: 9, color: 'text.disabled', textAlign: 'center' }}>
                  {Number(otherMin).toLocaleString(undefined, { maximumFractionDigits: 0 })} → {Number(otherMax).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </Typography>
              </Box>
            )}
            {/* Fallback: only non-% axis exists */}
            {!hasPctAxis && hasOtherAxis && (
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.25 }}>
                  <Typography sx={{ fontSize: 9, color: 'text.disabled', flex: 1 }}>Y — Range ({otherSliderUnit})</Typography>
                  <ResetButton onClick={resetY2} disabled={isY2Reset} />
                </Box>
                <Slider value={y2Range} onChange={(_, v) => setY2Range(v)} size="small" disableSwap
                  sx={{ color: 'warning.main', height: 3, '& .MuiSlider-thumb': { width: 10, height: 10 }, '& .MuiSlider-rail': { opacity: 0.2 } }}
                />
                <Typography sx={{ fontSize: 9, color: 'text.disabled', textAlign: 'center' }}>
                  {Number(otherMin).toLocaleString(undefined, { maximumFractionDigits: 0 })} → {Number(otherMax).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </Typography>
              </Box>
            )}
            </Box>
          </Box>
        )}
      </Box>

      <Dialog open={open} onClose={closeModal} maxWidth="xl" fullWidth
        PaperProps={{ sx: { borderRadius: '16px', m: 2 } }}>
        <DialogContent sx={{ p: '20px 24px 24px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#2d3748' }}>{title}</Typography>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Tooltip title="Toggle sliders">
                <IconButton size="small" onClick={() => setSliders(v => !v)}
                  sx={{ color: sliders ? 'primary.main' : 'text.disabled', '&:hover': { color: 'primary.main' } }}>
                  <TuneIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Export chart">
                <span>
                  <IconButton size="small" onClick={e => setExportAnchor(e.currentTarget)} disabled={exporting}
                    sx={{ color: 'text.disabled', '&:hover': { color: 'error.main' } }}>
                    {exporting ? <CircularProgress size={16} /> : <DownloadIcon sx={{ fontSize: 18 }} />}
                  </IconButton>
                </span>
              </Tooltip>
              <Menu anchorEl={exportAnchor} open={Boolean(exportAnchor)} onClose={() => setExportAnchor(null)}
                PaperProps={{ sx: { borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,.12)' } }}>
                <MenuItem onClick={exportChartPDF} sx={{ fontSize: 12, gap: 1 }}>
                  <Box sx={{ width: 20, height: 20, bgcolor: '#c53030', borderRadius: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography sx={{ fontSize: 8, color: '#fff', fontWeight: 700 }}>PDF</Typography>
                  </Box>
                  Export as PDF
                </MenuItem>
                <MenuItem onClick={exportChartPPT} sx={{ fontSize: 12, gap: 1 }}>
                  <Box sx={{ width: 20, height: 20, bgcolor: '#d04423', borderRadius: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography sx={{ fontSize: 8, color: '#fff', fontWeight: 700 }}>PPT</Typography>
                  </Box>
                  Export as PowerPoint
                </MenuItem>
                <MenuItem onClick={exportChartPNG} sx={{ fontSize: 12, gap: 1 }}>
                  <Box sx={{ width: 20, height: 20, bgcolor: '#3182ce', borderRadius: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography sx={{ fontSize: 8, color: '#fff', fontWeight: 700 }}>PNG</Typography>
                  </Box>
                  Export as Image
                </MenuItem>
              </Menu>
              <IconButton size="small" onClick={closeModal} sx={{ color: 'text.disabled' }}>
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '65vh' }}>
            {/* Modal sliders */}
            {sliders && (
              <Box sx={{ px: 1, pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mb: 0.25 }}>
                  <Button
                    size="small"
                    onClick={resetAll}
                    disabled={allReset}
                    startIcon={<RestartAltIcon sx={{ fontSize: 13 }} />}
                    sx={{
                      py: 0.25, px: 0.75, fontSize: 9, textTransform: 'none',
                      color: allReset ? 'text.disabled' : 'text.secondary',
                      '&:hover': { color: 'primary.main', bgcolor: 'transparent' },
                    }}
                  >
                    Reset all
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.25 }}>
                    <Typography sx={{ fontSize: 9, color: 'text.disabled', flex: 1 }}>X — Date range</Typography>
                    <ResetButton onClick={resetX} disabled={isXReset} />
                  </Box>
                  <Slider value={range} onChange={(_, v) => handleRangeChange(v)} size="small" disableSwap
                    sx={{ color: 'primary.main', height: 3, '& .MuiSlider-thumb': { width: 10, height: 10 }, '& .MuiSlider-rail': { opacity: 0.2 } }} />
                  <Typography sx={{ fontSize: 9, color: 'text.disabled', textAlign: 'center' }}>
                    {slicedLabels[0] ?? ''} → {slicedLabels[slicedLabels.length - 1] ?? ''}
                  </Typography>
                </Box>
                {hasPctAxis && (
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.25 }}>
                      <Typography sx={{ fontSize: 9, color: 'text.disabled', flex: 1 }}>
                        Y1 — Range {dualAxis ? `(${pctSliderUnit})` : ''}
                      </Typography>
                      <ResetButton onClick={resetY1} disabled={isY1Reset} />
                    </Box>
                    <Slider value={yRange} onChange={(_, v) => setYRange(v)} size="small" disableSwap
                      sx={{ color: 'secondary.main', height: 3, '& .MuiSlider-thumb': { width: 10, height: 10 }, '& .MuiSlider-rail': { opacity: 0.2 } }} />
                    <Typography sx={{ fontSize: 9, color: 'text.disabled', textAlign: 'center' }}>
                      {pctMin.toFixed(2)} → {pctMax.toFixed(2)}
                    </Typography>
                  </Box>
                )}
                {dualAxis && (
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.25 }}>
                      <Typography sx={{ fontSize: 9, color: 'text.disabled', flex: 1 }}>Y2 — Range ({otherSliderUnit})</Typography>
                      <ResetButton onClick={resetY2} disabled={isY2Reset} />
                    </Box>
                    <Slider value={y2Range} onChange={(_, v) => setY2Range(v)} size="small" disableSwap
                      sx={{ color: 'warning.main', height: 3, '& .MuiSlider-thumb': { width: 10, height: 10 }, '& .MuiSlider-rail': { opacity: 0.2 } }} />
                    <Typography sx={{ fontSize: 9, color: 'text.disabled', textAlign: 'center' }}>
                      {Number(otherMin).toLocaleString(undefined, { maximumFractionDigits: 0 })} → {Number(otherMax).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </Typography>
                  </Box>
                )}
                {!hasPctAxis && hasOtherAxis && (
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.25 }}>
                      <Typography sx={{ fontSize: 9, color: 'text.disabled', flex: 1 }}>Y — Range ({otherSliderUnit})</Typography>
                      <ResetButton onClick={resetY2} disabled={isY2Reset} />
                    </Box>
                    <Slider value={y2Range} onChange={(_, v) => setY2Range(v)} size="small" disableSwap
                      sx={{ color: 'warning.main', height: 3, '& .MuiSlider-thumb': { width: 10, height: 10 }, '& .MuiSlider-rail': { opacity: 0.2 } }} />
                    <Typography sx={{ fontSize: 9, color: 'text.disabled', textAlign: 'center' }}>
                      {Number(otherMin).toLocaleString(undefined, { maximumFractionDigits: 0 })} → {Number(otherMax).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </Typography>
                  </Box>
                )}
                </Box>
              </Box>
            )}
            <Box sx={{ display: 'flex', gap: 2, flex: 1, minHeight: 0 }}>
            <Box
              ref={modalChartBoxRef}
              onClick={e => {
                const ci = modalLineRef.current?.chartInstance
                if (!ci || !modalChartBoxRef.current) return
                const rect  = modalChartBoxRef.current.getBoundingClientRect()
                const clickX = e.clientX - rect.left
                const clickY = e.clientY - rect.top
                const pts   = ci.getElementsAtXAxis(e.nativeEvent)
                if (!pts?.length) {
                  ci.options.tooltips.enabled = true; ci.update(0)
                  setModalPinnedTooltip(null); return
                }
                const idx   = pts[0]._index
                const label = ci.data.labels[idx]
                const ptX   = pts[0]._view?.x ?? clickX
                const ptY   = pts[0]._view?.y ?? clickY
                const items = ci.data.datasets
                  .filter(ds => !ds.hidden)
                  .map(ds => ({ key: ds.label, value: ds.data[idx], color: ds.borderColor }))
                  .filter(it => it.value != null)
                ci.options.tooltips.enabled = false; ci.update(0)
                setModalPinnedTooltip({ label, items, x: ptX, y: ptY })
              }}
              sx={{ flex: 1, position: 'relative' }}>
              {/* Watermark */}
              <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', pointerEvents: 'none', zIndex: 0, width: '40%', display: 'flex', justifyContent: 'center', opacity: 0.05 }}>
                <img src={celfinetLogo} alt="" style={{ width: '100%' }} />
              </Box>
              <Line key={axisKey} ref={modalLineRef} data={{ labels: slicedLabels, datasets: chartDatasets }} options={options} />
              {modalPinnedTooltip && (
                <Box sx={{
                  position: 'absolute',
                  left: modalPinnedTooltip.x + 8,
                  top:  Math.max(modalPinnedTooltip.y - 20, 0),
                  transform: modalPinnedTooltip.x > 300 ? 'translateX(-100%)' : 'none',
                  zIndex: 10, pointerEvents: 'auto',
                  bgcolor: 'rgba(255,255,255,0.97)', border: '1px solid', borderColor: 'divider',
                  borderRadius: 1.5, p: '8px 10px', boxShadow: '0 4px 12px rgba(0,0,0,.15)', minWidth: 140,
                }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary' }}>{modalPinnedTooltip.label}</Typography>
                    <Box onClick={e => {
                      e.stopPropagation()
                      const ci = modalLineRef.current?.chartInstance
                      if (ci) { ci.options.tooltips.enabled = true; ci.update(0) }
                      setModalPinnedTooltip(null)
                    }} sx={{ fontSize: 12, cursor: 'pointer', color: 'text.disabled', ml: 1, '&:hover': { color: 'text.primary' } }}>✕</Box>
                  </Box>
                  {modalPinnedTooltip.items.map(it => (
                    <Box key={it.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: '1px' }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: it.color, flexShrink: 0 }} />
                      <Typography sx={{ fontSize: 11, color: 'text.secondary', flex: 1 }}>{it.key}</Typography>
                      <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'text.primary' }}>{Number(it.value).toFixed(2)}</Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
            <Box ref={legendRef}>
              <ModalLegend
                series={series}
                visibleSeries={visibleSeries}
                onToggle={handleToggle}
                onClear={resetSeriesFilter}
                hasRag={ragDatasets.length > 0}
              />
            </Box>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  )
}